import { Button } from '@1trading/react/components/ui/button';
import { Loading } from '@1trading/react/components/ui/loading';
import { Separator } from '@1trading/react/components/ui/separator';
import { useToast } from '@1trading/react/hooks/use-toast';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { GuardedFormStepper, useGuardedFormStepper } from '@/components/ui/guarded-form-stepper';
import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeading, PageHeadingBackButton, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { FundsOwnership } from '@/constants/institution';
import { InstitutionOnboardingProgressStep, useProgress } from '@/context/progress';
import {
  INSTITUTION_BUSINESS_PROFILE_DEFAULTS,
  type InstitutionBusinessProfile,
  institutionBusinessProfileOverviewSchema,
  institutionBusinessProfileToInstitutionDetailsBusinessProfileApi,
  institutionDetailsBusinessProfileApiToInstitutionBusinessProfile,
} from '@/features/institution/business-profile/schema';
import { InstitutionGoToReviewStepperButton } from '@/features/institution/components/go-to-review-button';
import { useInstitutionOnboardingBasePath } from '@/features/institution/context/update';
import { useDetailsFlowControl } from '@/features/institution/hooks/useDetailsFlowControl';
import { useInstitutionDetailsMutation, useInstitutionDetailsV2Answers, useInstitutionDetailsVersion } from '@/features/institution/hooks/useInstitutionDetails';
import { getDetailsKeyProgress, INSTITUTION_DETAILS_TYPE } from '@/features/institution/utils';
import type { FormStepGuard } from '@/hooks/use-form-step-guard';
import { institutionDetailsV2BusinessProfileClientsSchema } from '@/services/api/profile';
import type { FormStep } from '@/types/components';
import { InstitutionBusinessProfileFieldsetBreakdown } from './fieldsets/breakdown';
import { InstitutionBusinessProfileFieldsetClients } from './fieldsets/clients';
import { InstitutionBusinessProfileFieldsetFundsOwnership } from './fieldsets/funds-ownership';
import { InstitutionBusinessProfileFieldsetSize } from './fieldsets/size';
import { InstitutionBusinessProfileFieldsetTopExchanges } from './fieldsets/top-exchanges';

const InstitutionBusinessProfileFormStepOverviewLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('institutionDetails.businessProfile.title')}</PageHeadingTitle>
      </legend>
    </PageHeading>
  );
};
InstitutionBusinessProfileFormStepOverviewLegend.displayName = 'InstitutionBusinessProfileFormStepOverviewLegend';

const InstitutionBusinessProfileFormStepOverview = () => (
  <div className="space-y-6">
    <InstitutionBusinessProfileFieldsetSize />
    <Separator />
    <InstitutionBusinessProfileFieldsetTopExchanges />
    <Separator />
    <InstitutionBusinessProfileFieldsetFundsOwnership />
  </div>
);
InstitutionBusinessProfileFormStepOverview.displayName = 'InstitutionBusinessProfileFormStepOverview';

// A company trading only its own funds has no client questions, so it finishes here
const beforeOverviewStepLeave: FormStepGuard<InstitutionBusinessProfile> = async ({ form, onSubmit }, next) => {
  if (!form) return next(false);

  if (form.getValues('fundsOwnership') === FundsOwnership.OWN) {
    if (await form.trigger()) await onSubmit(form.getValues());

    return next(false);
  }

  return next();
};

InstitutionBusinessProfileFormStepOverview.beforeStepLeave = beforeOverviewStepLeave;

const InstitutionBusinessProfileFormStepClientsLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('institutionDetails.businessProfile.clients.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('institutionDetails.businessProfile.clients.description')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
InstitutionBusinessProfileFormStepClientsLegend.displayName = 'InstitutionBusinessProfileFormStepClientsLegend';

const InstitutionBusinessProfileFormStepClients = () => (
  <div className="space-y-6">
    <InstitutionBusinessProfileFieldsetClients />
    <Separator />
    <InstitutionBusinessProfileFieldsetBreakdown name="clientGeographyBreakdown" />
    <Separator />
    <InstitutionBusinessProfileFieldsetBreakdown name="clientRiskBreakdown" />
  </div>
);
InstitutionBusinessProfileFormStepClients.displayName = 'InstitutionBusinessProfileFormStepClients';

const FORM_STEPS = {
  OVERVIEW: {
    Component: InstitutionBusinessProfileFormStepOverview,
    Legend: InstitutionBusinessProfileFormStepOverviewLegend,
    schema: institutionBusinessProfileOverviewSchema,
  },
  CLIENTS: {
    Component: InstitutionBusinessProfileFormStepClients,
    Legend: InstitutionBusinessProfileFormStepClientsLegend,
    schema: institutionDetailsV2BusinessProfileClientsSchema,
  },
} satisfies Record<string, FormStep<InstitutionBusinessProfile>>;

const STEPS = Object.values(FORM_STEPS).map((step) => step.Component);
const STEP_LEGENDS = Object.values(FORM_STEPS).map((step) => step.Legend);
const STEP_SCHEMAS = Object.values(FORM_STEPS).map((step) => step.schema);

const InstitutionBusinessProfileFormCurrentStep = () => {
  const { currentStep, totalSteps } = useGuardedFormStepper<InstitutionBusinessProfile>();
  const { data: version } = useInstitutionDetailsVersion();
  const { setCurrentStep } = useProgress();
  const Step = STEPS[currentStep];

  useEffect(() => {
    if (!version) return;

    return setCurrentStep(
      InstitutionOnboardingProgressStep.INSTITUTION_INFORMATION,
      getDetailsKeyProgress(INSTITUTION_DETAILS_TYPE.ONBOARDING, version, 'businessProfile', currentStep, totalSteps),
    );
  }, [currentStep, setCurrentStep, totalSteps, version]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to scroll when currentStep changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  if (!Step) throw new Error('Step not found');

  return (
    <PageCardContent className="pt-8">
      <div className="animate-in fade-in" key={currentStep}>
        <Step />
      </div>
    </PageCardContent>
  );
};
InstitutionBusinessProfileFormCurrentStep.displayName = 'InstitutionBusinessProfileFormCurrentStep';

const InstitutionBusinessProfileFormCurrentStepLegend = () => {
  const { currentStep } = useGuardedFormStepper<InstitutionBusinessProfile>();
  const Legend = STEP_LEGENDS[currentStep];

  if (!Legend) throw new Error('Legend not found');

  return <Legend />;
};
InstitutionBusinessProfileFormCurrentStepLegend.displayName = 'InstitutionBusinessProfileFormCurrentStepLegend';

const InstitutionBusinessProfileFormBackButton = () => {
  const { currentStep, prevStep } = useGuardedFormStepper<InstitutionBusinessProfile>();
  const navigate = useNavigate();
  const onboardingPath = useInstitutionOnboardingBasePath();

  const handlePrevStep = useCallback(() => {
    if (currentStep === 0) {
      navigate(`${onboardingPath}/company`, { replace: true });
    } else {
      prevStep();
    }
  }, [currentStep, prevStep, navigate, onboardingPath]);

  return <PageHeadingBackButton floatingInPage="left" fixedInLayout onClick={handlePrevStep} />;
};
InstitutionBusinessProfileFormBackButton.displayName = 'InstitutionBusinessProfileFormBackButton';

const InstitutionBusinessProfileFormFooter = ({ isPending }: { isPending: boolean }) => {
  const { currentStep, nextStep } = useGuardedFormStepper<InstitutionBusinessProfile>();
  const { t } = useTranslation();
  const Step = STEPS[currentStep];

  if (Step && 'isAutoNextStep' in Step && Step.isAutoNextStep) return null;

  return (
    <PageCardFooter>
      <Button className="w-full relative" size="lg" variant="primary" disabled={isPending} onClick={() => nextStep()}>
        <span className={isPending ? 'invisible' : ''}>{t('buttons.continue')}</span>
        {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
      </Button>
    </PageCardFooter>
  );
};
InstitutionBusinessProfileFormFooter.displayName = 'InstitutionBusinessProfileFormFooter';

const InstitutionBusinessProfileForm = () => {
  const { isLoading: isLoadingInstitutionDetailsAnswers, data: institutionDetailsAnswers } = useInstitutionDetailsV2Answers();
  const { isLoading: isLoadingInstitutionDetailsFlow } = useDetailsFlowControl(INSTITUTION_DETAILS_TYPE.ONBOARDING, 'businessProfile');
  const { isPending, mutateAsync } = useInstitutionDetailsMutation();
  const navigate = useNavigate();
  const onboardingPath = useInstitutionOnboardingBasePath();
  const { toast } = useToast();
  const { t } = useTranslation();
  const isLoading = isLoadingInstitutionDetailsAnswers || isLoadingInstitutionDetailsFlow;

  const onSubmit = async (data: InstitutionBusinessProfile, { isReturningToReview = false }: { isReturningToReview?: boolean } = {}) => {
    await mutateAsync(
      { businessProfile: await institutionBusinessProfileToInstitutionDetailsBusinessProfileApi(data) },
      {
        onError: () => {
          toast({
            description: t('fundamentals.defaultError.message'),
            variant: 'destructive',
          });
        },
        onSuccess: () => {
          navigate(isReturningToReview ? `${onboardingPath}/review` : `${onboardingPath}/ownership`, { replace: true });
        },
      },
    );
  };

  if (isLoading) return <LoadingCard />;

  const defaultValues = institutionDetailsAnswers?.businessProfile
    ? institutionDetailsBusinessProfileApiToInstitutionBusinessProfile(institutionDetailsAnswers.businessProfile)
    : INSTITUTION_BUSINESS_PROFILE_DEFAULTS;

  return (
    <GuardedFormStepper className="relative" defaultValues={defaultValues} steps={STEPS} stepSchemas={STEP_SCHEMAS} onSubmit={onSubmit}>
      <fieldset>
        <InstitutionBusinessProfileFormCurrentStepLegend />
        <div className="flex flex-row justify-between -md:justify-end">
          <InstitutionBusinessProfileFormBackButton />
          <InstitutionGoToReviewStepperButton onSubmit={(data: InstitutionBusinessProfile) => onSubmit(data, { isReturningToReview: true })} />
        </div>
        <PageCard size="lg" bordered fixedFooter>
          <InstitutionBusinessProfileFormCurrentStep />
          <InstitutionBusinessProfileFormFooter isPending={isPending} />
        </PageCard>
      </fieldset>
    </GuardedFormStepper>
  );
};
InstitutionBusinessProfileForm.displayName = 'InstitutionBusinessProfileForm';

export default InstitutionBusinessProfileForm;
